import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code, Database, Zap, Settings, FileText } from 'lucide-react';

const TechStack = ({ data = {} }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const categories = [
    { 
      title: 'Frontend', 
      icon: Code, 
      techs: data.frontend || []
    },
    { 
      title: 'Backend', 
      icon: Database, 
      techs: data.backend || []
    },
    { 
      title: 'AI/ML', 
      icon: Zap, 
      techs: data.aiml || []
    },
    { 
      title: 'Cloud & DevOps', 
      icon: Settings, 
      techs: data.cloud || []
    },
    { 
      title: 'Practices', 
      icon: FileText, 
      techs: data.practices || []
    }
  ];


  return (
    <section id="tech-stack" className="py-24 relative overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/20 to-slate-900/15" />
        <div className="absolute top-24 right-24 w-64 h-64 bg-slate-400/3 rounded-full blur-3xl" />
        <div className="absolute bottom-24 left-24 w-80 h-80 bg-slate-500/2 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10" ref={ref}>
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Code className="h-8 w-8 text-slate-400" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50">
              Tech Stack
            </h2>
          </div>
          <motion.div 
            className="w-16 h-0.5 bg-slate-500 mx-auto mb-6"
            initial={{ width: 0 }}
            animate={inView ? { width: 64 } : { width: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Technologies I use to build AI-integrated systems and thoughtful product experiences
          </p>
        </motion.div>

        {/* Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, index) => {
            const IconComponent = category.icon;
            return (
              <motion.div
                key={index}
                className="glass-card p-8 group"
                initial={{ y: 30, opacity: 0 }}
                animate={inView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <IconComponent className="h-6 w-6 text-slate-400" />
                    <h3 className="text-xl font-semibold text-slate-50">
                      {category.title}
                    </h3>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {category.techs.map((tech, techIndex) => (
                    <motion.span
                      key={techIndex}
                      className="px-3 py-1.5 bg-slate-800/50 text-slate-300 rounded-lg text-sm border border-slate-700/50 hover:border-slate-500/50 hover:text-slate-200 transition-colors cursor-default"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                      transition={{ delay: index * 0.1 + techIndex * 0.05 + 0.3, duration: 0.4 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TechStack;